/**
 * Utility functions for making authenticated API calls
 * Auth uses HttpOnly cookie (auth_token); credentials: include sends it automatically.
 * getToken remains for legacy/API clients that use Bearer token.
 */
import axios from 'axios';

// Get token from storage (legacy; prefer cookie auth for web)
export const getToken = () => {
  return localStorage.getItem("token") || sessionStorage.getItem("token");
};

// Create authenticated headers (includes Bearer if token in storage, for API clients)
export const createAuthHeaders = (additionalHeaders = {}) => {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...additionalHeaders,
  };
};

// Determine API URL based on environment
const getApiUrl = () => {
  // If env variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Custom domain - route API calls to the service subdomain
  if (window.location.hostname === 'getrepcheck.com' || window.location.hostname === 'www.getrepcheck.com') {
    return 'https://service.getrepcheck.com';
  }

  // In production (when hostname is not localhost), use the same host
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return `${window.location.protocol}//${window.location.hostname}:4000`;
  }

  // Default to localhost for development
  return 'http://localhost:4000';
};

// Axios instance with interceptors
const apiClient = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // Send HttpOnly auth cookie
});

// Request interceptor: add Bearer token if in storage (API clients), else cookie is used
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, clear any legacy storage and notify logout
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      window.dispatchEvent(new CustomEvent('auth-logout'));
      throw new Error('Authentication required');
    }
    return Promise.reject(error);
  }
);

// Authenticated request wrapper
export const authenticatedFetch = async (url, options = {}) => {
  const { headers = {}, ...restOptions } = options;

  const response = await apiClient.request({
    url,
    headers: {
      ...createAuthHeaders(),
      ...headers,
    },
    ...restOptions,
  });

  return response;
};

// API helper functions
export const apiUtils = {
  get: async (url, options = {}) => {
    const { params, ...restOptions } = options;

    const response = await apiClient.get(url, {
      params,
      ...restOptions,
    });

    return {
      json: async () => response.data,
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      ...response,
    };
  },

  post: async (url, data, options = {}) => {
    const response = await apiClient.post(url, data, options);

    return {
      json: async () => response.data,
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      ...response,
    };
  },

  put: async (url, data, options = {}) => {
    const response = await apiClient.put(url, data, options);

    return {
      json: async () => response.data,
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      ...response,
    };
  },

  delete: async (url, options = {}) => {
    const response = await apiClient.delete(url, options);

    return {
      json: async () => response.data,
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      ...response,
    };
  },
};

// Simple apiCall function for compatibility
export const apiCall = async (url, options = {}) => {
  const { method = 'GET', body, ...restOptions } = options;

  let response;
  if (method === 'GET') {
    response = await apiUtils.get(url, restOptions);
  } else if (method === 'POST') {
    response = await apiUtils.post(url, body, restOptions);
  } else if (method === 'PUT') {
    response = await apiUtils.put(url, body, restOptions);
  } else if (method === 'DELETE') {
    response = await apiUtils.delete(url, restOptions);
  }

  return await response.json();
};

export default apiUtils;
